import { css } from '@emotion/react';
import styled from '@emotion/styled';
import Image from 'next/image';
import introBg01Mobile from '../comm/images/intro-bg-01-mobile.png';
import introBg01 from '../comm/images/intro-bg-01.png';
import introBg02Mobile from '../comm/images/intro-bg-02-mobile.png';
import introBg02 from '../comm/images/intro-bg-02.png';
import { InlineBlock } from '@/components/comm/InlineBlock';
import { Section } from '@/components/pages/about/comm/Layout';
import useCustomBreakpoint, { breakpointMdSmall } from '@/components/pages/about/comm/lib/custom-breakpoint.service';
import { body03, headline04 } from '@/components/pages/about/styles/typography';
import { breakpointMedium, breakpointSmall } from '@/constants/breakpoints';
import { colors } from '@/constants/colors';

const contents = [
  {
    id: 'coding-test',
    style: {
      backgroundColor: colors.blue25,
      accentColor: colors.primary,
      backgroundImage: (breakpoint: ReturnType<typeof useCustomBreakpoint>) =>
        breakpoint === 'sm' ? introBg01Mobile : introBg01,
    },
    text: {
      title: (
        <>
          인증시험과 함께라면{' '}
          <InlineBlock
            css={css`
              color: ${colors.primary};
            `}
          >
            코딩테스트도 두렵지 않아요.
          </InlineBlock>
        </>
      ),
      description: (breakpoint: ReturnType<typeof useCustomBreakpoint>) => (
        <>
          프로그래머스의 2만번의 코딩테스트 운영 및 출제 노하우로 구성된 문제로,{' '}
          {breakpoint === 'sm' ? null : <br />}
          자격증 취득만으로 다양한 기업에서 출제하는 코딩테스트를 대비할 수
          있어요.
        </>
      ),
    },
  },
  {
    id: 'career',
    style: {
      backgroundColor: '#EBFAE8',
      accentColor: colors.success,
      backgroundImage: (breakpoint: ReturnType<typeof useCustomBreakpoint>) =>
        breakpoint === 'sm' ? introBg02Mobile : introBg02,
    },
    text: {
      title: (
        <>
          <span
            css={css`
              color: ${colors.success};
            `}
          >
            국내 유일 IT 실무 기반 자격증
          </span>
          으로 채용 시 유리해요.
        </>
      ),
      description: (breakpoint: ReturnType<typeof useCustomBreakpoint>) => (
        <>
          1400개 기업의 실무 역량을 분석해서 제출된 문제로, 다양한 기업에서 인재
          채용에 활용하고 있습니다. {breakpoint === 'sm' ? null : <br />}
          여러분의 역량을 입증하고 채용 경쟁력을 한 단계 높여보세요.
        </>
      ),
    },
  },
] as const;

const IntroSection = () => {
  const breakpoint = useCustomBreakpoint();

  return (
    <Section.Container>
      <StyledSectionInner>
        <Section.Hgroup>
          <Section.Title>
            프로그래머스 자격증으로 내 실력을 증명하세요.
          </Section.Title>
          <Section.Subtitle>
            업계 최초 &apos;소프트웨어 역량 개발 연구소&apos;, 국내 최고의
            권위자가 만든 자격증으로 내 코딩 실력을 증명하세요.
          </Section.Subtitle>
        </Section.Hgroup>

        <List>
          {contents.map(({ id, style, text }) => (
            <ContentItem key={id} backgroundColor={style.backgroundColor}>
              <ContentHgroup>
                <ContentTitle>{text.title}</ContentTitle>
                <ContentDescription>
                  {text.description(breakpoint)}
                </ContentDescription>
              </ContentHgroup>

              <Figure
                css={id === 'coding-test' && css`
                margin-top: -64px;

                @media (max-width: ${breakpointMedium}) {
                  margin-top: 0px;
                }

                @media (max-width: ${breakpointMdSmall}) {
                  margin-top: 24px;
                }
              ` || css`
                margin-top: 24px;
              `}
              >
                <Image
                  src={style.backgroundImage(breakpoint)}
                  alt="배경 이미지"
                />
              </Figure>
            </ContentItem>
          ))}
        </List>
      </StyledSectionInner>
    </Section.Container>
  );
};

export default IntroSection;

const StyledSectionInner = styled(Section.Inner)`
  display: flex;
  flex-direction: column;
  padding: 120px 16px 60px;
  gap: 40px;
  
  @media (max-width: ${breakpointMedium}) { 
    padding: 120px 20px 60px;
  }
`;

const List = styled.ul`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: center;
  padding: 0;
  margin: 0;
  gap: 32px;
  list-style: none;

  @media (max-width: ${breakpointMedium}) {
    gap: 24px;
  }
`;

type ContentItemProps = {
  backgroundColor: string,
};

const ContentItem = styled.li<ContentItemProps>`
  position: relative;
  display: flex;
  overflow: hidden;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  background-color: ${({ backgroundColor }) => backgroundColor};
  border-radius: 32px;

  @media (max-width: ${breakpointMdSmall}) {
    border-radius: 24px;
  }
`;

const Figure = styled.figure`
display: flex;
width: 100%;
justify-content: flex-end;

@media (max-width: ${breakpointSmall}) {
  justify-content: center;
}

> img {
  display: block;
  width: 100%;
  max-width: 1160px;
  height: 100%;

  @media (max-width: ${breakpointSmall}) {
    height: 177px;
    object-fit: cover;
  } 
}
`;

const ContentHgroup = styled.hgroup`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  padding: 48px 48px 0 48px;
  gap: 8px;

  @media (max-width: ${breakpointMdSmall}) {
    padding: 32px 24px 0 24px;
  }
`;

const ContentTitle = styled.h3`
  ${headline04}
  color: ${colors.textPrimary};
`;

const ContentDescription = styled.p`
  ${body03}
  color: ${colors.textPrimary};
`;
